import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface ActivityEventPayload {
  work_type?: string;
  work_id?: string;
  event?: string;
  phase?: string;
  branch?: string;
  pipeline_name?: string;
  phase_next?: string;
  duration_seconds?: number;
  gate_type?: string;
  gate_result?: string;
  run_id?: string;
  cost_usd?: number;
  by_model?: Record<string, number>;
  tool_fail_ratio?: number;
  edit_revert_files?: number;
  max_gate_fail_streak?: number;
  issue?: string;
  jira_key?: string;
  cost_breakdown?: Record<string, number>;
  tags?: string[];
  repo_slug?: string;
  agent_session_id?: string;
  // governance_audit fields
  subject_id?: string;
  decision?: string;
  // rubric_verdict fields
  skill?: string;
  verdict?: string;
  // actor identifier (fallback when top-level actor_kind not present)
  actor?: string;
  [key: string]: unknown;
}

export interface ActivityEvent {
  event_id: string | null;
  event_type: string;
  title: string;
  summary: string;
  actor: string;
  actor_kind?: "human" | "agent" | "engine";
  recorded_at: string;
  payload?: ActivityEventPayload;
}

export interface ActivityDigest {
  patterns: Record<string, number>;
  governance: Record<string, number>;
  knowledge: Record<string, number>;
  members: Record<string, number>;
  workflow: Record<string, number>;
  pipeline: Record<string, number>;
  sessions: Record<string, number>;
  tooling: Record<string, number>;
}

export interface ActivityFeedResponse {
  events: ActivityEvent[];
  digest?: ActivityDigest;
  highlights?: ActivityEvent[];
  total: number;
  limit: number;
  offset: number;
  server_time?: string;
}

const ACTIVITY_PARAMS = { limit: 2000 } as const;

export function useProjectActivity(slug: string) {
  const query = useQuery({
    queryKey: queryKeys.projects.activity(slug, ACTIVITY_PARAMS),
    queryFn: () =>
      api<ActivityFeedResponse>(
        `/api/v2/activity?project_id=${encodeURIComponent(slug)}&limit=${ACTIVITY_PARAMS.limit}`,
      ),
    enabled: slug.length > 0,
  });

  return {
    events: query.data?.events ?? [],
    digest: query.data?.digest ?? null,
    highlights: query.data?.highlights ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    error: query.error ? "Failed to load activity" : "",
    refetch: query.refetch,
  };
}
